import { createSlice } from '@reduxjs/toolkit'
import { themeConfig } from 'configs/theme.config'
import {
    getDefaultTicketWorkPods,
    normalizeTicketWorkPods,
} from 'constants/ticketWorkPods'
import {
    LAYOUT_TYPE_MODERN,
    LAYOUT_TYPE_CLASSIC,
    LAYOUT_TYPE_STACKED_SIDE,
    NAV_MODE_TRANSPARENT,
    NAV_MODE_LIGHT,
    NAV_MODE_DARK,
    NAV_MODE_THEMED,
    MODE_DARK,
    MODE_LIGHT,
    LAYOUT_TYPE_DECKED,
} from 'constants/theme.constant'

const initialNavMode = () => {
    if (
        themeConfig.layout.type === LAYOUT_TYPE_MODERN &&
        themeConfig.navMode !== NAV_MODE_THEMED
    ) {
        return NAV_MODE_TRANSPARENT
    }

    return themeConfig.navMode
}

const initialState = {
    themeColor: themeConfig.themeColor,
    direction: themeConfig.direction,
    mode: themeConfig.mode,
    primaryColorLevel: themeConfig.primaryColorLevel,
    panelExpand: themeConfig.panelExpand,
    navMode: initialNavMode(),
    layout: themeConfig.layout,
    ticketWorkPods: normalizeTicketWorkPods(themeConfig.ticketWorkPods),
}

const availableNavColorLayouts = [
    LAYOUT_TYPE_CLASSIC,
    LAYOUT_TYPE_STACKED_SIDE,
    LAYOUT_TYPE_DECKED,
]

export const themeSlice = createSlice({
    name: 'theme',
    initialState,
    reducers: {
        setDirection: (state, action) => {
            state.direction = action.payload
        },
        setMode: (state, action) => {
            const availableColorNav = availableNavColorLayouts.includes(
                state.layout.type
            )

            if (
                availableColorNav &&
                action.payload === MODE_DARK &&
                state.navMode !== NAV_MODE_THEMED
            ) {
                state.navMode = NAV_MODE_DARK
            }
            if (
                availableColorNav &&
                action.payload === MODE_LIGHT &&
                state.navMode !== NAV_MODE_THEMED
            ) {
                state.navMode = NAV_MODE_LIGHT
            }
            state.mode = action.payload
        },
        setLayout: (state, action) => {
            state.cardBordered = action.payload === LAYOUT_TYPE_MODERN
            if (action.payload === LAYOUT_TYPE_MODERN) {
                state.navMode = NAV_MODE_TRANSPARENT
            }

            const availableColorNav = availableNavColorLayouts.includes(
                action.payload
            )

            if (availableColorNav && state.mode === MODE_LIGHT) {
                state.navMode = NAV_MODE_LIGHT
            }

            if (availableColorNav && state.mode === MODE_DARK) {
                state.navMode = NAV_MODE_DARK
            }

            state.layout = {
                ...state.layout,
                ...{ type: action.payload },
            }
        },
        setPreviousLayout: (state, action) => {
            state.layout.previousType = action.payload
        },
        setSideNavCollapse: (state, action) => {
            state.layout = {
                ...state.layout,
                ...{ sideNavCollapse: action.payload },
            }
        },
        setNavMode: (state, action) => {
            if (action.payload !== 'default') {
                state.navMode = action.payload
            } else {
                if (state.layout.type === LAYOUT_TYPE_MODERN) {
                    state.navMode = NAV_MODE_TRANSPARENT
                }

                const availableColorNav = availableNavColorLayouts.includes(
                    state.layout.type
                )

                if (availableColorNav && state.mode === MODE_LIGHT) {
                    state.navMode = NAV_MODE_LIGHT
                }

                if (availableColorNav && state.mode === MODE_DARK) {
                    state.navMode = NAV_MODE_DARK
                }
            }
        },
        setPanelExpand: (state, action) => {
            state.panelExpand = action.payload
        },
        setThemeColor: (state, action) => {
            state.themeColor = action.payload
        },
        setThemeColorLevel: (state, action) => {
            state.primaryColorLevel = action.payload
        },
        setTicketWorkPodSize: (state, action) => {
            const { id, size } = action.payload || {}
            const pods = normalizeTicketWorkPods(state.ticketWorkPods)
            const pod = pods.find((item) => item.id === id)

            if (pod && size) {
                pod.size = size
            }

            state.ticketWorkPods = normalizeTicketWorkPods(pods)
        },
        setTicketWorkPodVisibility: (state, action) => {
            const { id, visible } = action.payload || {}
            const pods = normalizeTicketWorkPods(state.ticketWorkPods)
            const pod = pods.find((item) => item.id === id)

            if (pod && typeof visible === 'boolean') {
                pod.visible = visible
            }

            state.ticketWorkPods = pods
        },
        moveTicketWorkPod: (state, action) => {
            const { id, direction } = action.payload || {}
            const pods = normalizeTicketWorkPods(state.ticketWorkPods)
            const currentIndex = pods.findIndex((item) => item.id === id)
            const targetIndex =
                direction === 'up'
                    ? currentIndex - 1
                    : direction === 'down'
                      ? currentIndex + 1
                      : currentIndex

            if (
                currentIndex < 0 ||
                targetIndex < 0 ||
                targetIndex >= pods.length ||
                targetIndex === currentIndex
            ) {
                state.ticketWorkPods = pods
                return
            }

            const [pod] = pods.splice(currentIndex, 1)
            pods.splice(targetIndex, 0, pod)
            state.ticketWorkPods = pods
        },
        resetTicketWorkPods: (state) => {
            state.ticketWorkPods = getDefaultTicketWorkPods()
        },
    },
})

export const {
    setDirection,
    setMode,
    setLayout,
    setSideNavCollapse,
    setNavMode,
    setPanelExpand,
    setThemeColor,
    setThemeColorLevel,
    setPreviousLayout,
    setTicketWorkPodSize,
    setTicketWorkPodVisibility,
    moveTicketWorkPod,
    resetTicketWorkPods,
} = themeSlice.actions

export default themeSlice.reducer
